import {
    Box,
    Button,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from "@mui/material";
import axios from "../../../utils/axiosInstance";
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router";
import { MILLIS_TO_LOCALE } from "../../../utilities/helpers/formatUnix";
import RefreshIcon from "@mui/icons-material/RefreshRounded";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GeneralInfoModal from "../../UI/atoms/Popup/GeneralInfoModal";
import JSONPretty from "react-json-pretty";
import {
    INGESTION_PROGRESS_ERROR,
    INGESTION_PROGRESS_SUCCESS,
} from "../LandingPage/constants";

const PVCPackageLogs = () => {
    const sortInfo = {
        rowNo: "desc",
        status: null,
    };
    const [loader, setLoader] = useState(false);
    const [showInfoPopup, setShowInfoPopup] = useState(false); //show popup
    const [messageInfo, setMessageInfo] = useState(null); // show message on popup
    const [ingestionLogsList, setIngestionLogsList] = useState([]); // list
    const location = useLocation(); // for browser navigation
    const queryParams = new URLSearchParams(location.search);
    const logStream = queryParams.get("logStream");
    const ingestionName = queryParams.get("ingestionName");
    const ingestionTime = queryParams.get("ingestionTime");
    const executionIdFromQuery = queryParams.get("executionId");
const isVendorTransferPath = location.pathname.includes("vendor-transfer");
const logTypeFromQuery =
    queryParams.get("logType") ||
    (isVendorTransferPath ? "VENDOR_TRANSFER_LOG" : "XML_GENERATION_LOG");
const isVendorTransfer =
    isVendorTransferPath || logTypeFromQuery === "VENDOR_TRANSFER_LOG";
    const pageTitle = isVendorTransfer
        ? "Vendor Transfer Logs"
        : "Package Logs";
    const [sortKey, setSortKey] = useState<any>(sortInfo);
    const [executionId, setExecutionId] = useState<string | null>(executionIdFromQuery);

    // API call on the page load
    useEffect(() => {
        getIngestionLogsList();
    }, []);

    // call API for getting the ingestion logs list
    const getIngestionLogsList = async () => {
        try {
            setLoader(true);
            const ingestionResponse = await axios.get(
                `${process.env.BASE_URL}api/v1/cms/ingest/ingestion-logs?ingestionName=${logStream}&logType=${encodeURIComponent(logTypeFromQuery)}`
            );
            if (ingestionResponse.data && ingestionResponse.data?.success) {
                const list = ingestionResponse.data?.data || [];
                const executionLog = list.find(
                    (row: any) =>
                        row?.message?.event_name === "EXECUTION_STARTED" ||
                        row?.message?.additionalInfo?.executionId
                );
                setExecutionId(
                    executionLog?.message?.additionalInfo?.executionId ||
                        executionIdFromQuery ||
                        null
                );
                setIngestionLogsList(
                    list.filter(
                        (row: any) => row?.message?.event_name !== "EXECUTION_STARTED"
                    )
                );
                setSortKey(sortInfo);
            }
        } catch (error) {
            console.log("Error in getting getIngestionList information", error);
        } finally {
            setLoader(false);
        }
    };

    // set classname based on the error/success status
    const setClassName = (row: any) => {
        const status = row?.message?.event_name;
        if (status == INGESTION_PROGRESS_ERROR) {
            return "text-danger";
        } else if (status == INGESTION_PROGRESS_SUCCESS) {
            return "text-success";
        } else {
            return "";
        }
    };

    // refresh the list
    const handleRefresh = () => {
        getIngestionLogsList();
    };

    const openInfoModal = (row: any) => {
        setShowInfoPopup(true);
        if (typeof row?.message === "string") {
            setMessageInfo(row?.message);
        } else if (typeof row?.message === "object") {
            setMessageInfo(row?.message?.additionalInfo);
        }
    };

    const closeInfoModal = (row: any) => {
        setShowInfoPopup(false);
        setMessageInfo(null);
    };

    const sortColumn = (columnName: string) => {
        let ingestionList = [...ingestionLogsList];
        let sortOrder;
        if (sortKey[columnName] == "desc") {
            ingestionList.sort((a: any, b: any) =>
                a?.message?.additionalInfo?.[columnName] >
                b?.message?.additionalInfo?.[columnName]
                    ? -1
                    : 1
            );
            sortOrder = "asc";
        } else {
            ingestionList.sort((a: any, b: any) =>
                b?.message?.additionalInfo?.[columnName] <
                a?.message?.additionalInfo?.[columnName]
                    ? 1
                    : -1
            );
            sortOrder = "desc";
        }
        setIngestionLogsList([...ingestionList]);
        setSortKey({
            ...sortKey,
            [columnName]: sortOrder,
        });
    };

    return (
        <div className="config-heading w-80">
            <div>
                {/* Header section starts here */}
                <div className="ingest-header-refresh">
                    <h3>{pageTitle}</h3>
                </div>
                <div className="d-flex j-space">
                    <div>
                        <h5 className="m-0">File Name : {ingestionName}</h5>
                        <h5 className="m-0">
                            {isVendorTransfer
                                ? "Transfer Time"
                                : "Package Creation Time"}{" "}
                            : {MILLIS_TO_LOCALE(Number(ingestionTime), true)}
                        </h5>
                        {executionId ? (
                            <h5 className="m-0">Execution ID : {executionId}</h5>
                        ) : null}
                    </div>
                    <div>
                        <Button
                            disabled={loader}
                            onClick={handleRefresh}
                            className="float-right m-1"
                            variant="contained"
                        >
                            {loader ? (
                                <>
                                    Refreshing{" "}
                                    <CircularProgress
                                        size={20}
                                        color="secondary"
                                    />
                                </>
                            ) : (
                                "Refresh Logs"
                            )}
                        </Button>
                    </div>
                </div>
                {/* Header section ends here */}

                <div>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <TableSortLabel
                                            active
                                            direction={sortKey.rowNo}
                                            onClick={() => sortColumn("rowNo")}
                                        >
                                            <h3>Row No</h3>
                                        </TableSortLabel>
                                    </TableCell>
                                    {/* <TableCell align="left"><h3>Row No</h3></TableCell> */}
                                    <TableCell align="left">
                                        <h3>Content Type</h3>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active
                                            direction={
                                                sortKey.status || undefined
                                            }
                                            onClick={() => sortColumn("status")}
                                        >
                                            <h3>Status</h3>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell align="center">
                                        <h3>TimeStamp</h3>
                                    </TableCell>
                                    <TableCell align="center">
                                        <h3>Detailed Status</h3>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {ingestionLogsList &&
                                    ingestionLogsList.map(
                                        (row: any, index: number) => (
                                            <TableRow
                                                key={row.profileName}
                                                sx={{
                                                    "&:last-child td, &:last-child th":
                                                        { border: 0 },
                                                }}
                                            >
                                                <TableCell
                                                    width={200}
                                                    align="left"
                                                >
                                                    {row?.message
                                                        ?.additionalInfo
                                                        ?.rowNo || "-"}
                                                </TableCell>
                                                <TableCell
                                                    width={200}
                                                    align="left"
                                                >
                                                    {row?.message
                                                        ?.additionalInfo
                                                        ?.contentType || "-"}
                                                </TableCell>
                                                <TableCell
                                                    className={setClassName(
                                                        row
                                                    )}
                                                    width={200}
                                                    align="left"
                                                >
                                                    {row?.message
                                                        ?.additionalInfo
                                                        ?.status || "-"}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {MILLIS_TO_LOCALE(
                                                        row?.timestamp
                                                    )}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <span
                                                        className="cursor-pointer"
                                                        onClick={() =>
                                                            openInfoModal(row)
                                                        }
                                                    >
                                                        <VisibilityIcon />
                                                    </span>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {/* Modal for displaying detailed status -- starts here*/}
                    <GeneralInfoModal
                        title="Detailed Status"
                        isOpen={showInfoPopup}
                        handleClose={closeInfoModal}
                    >
                        <JSONPretty
                            id="json-pretty"
                            data={messageInfo}
                            style={{ fontSize: "0.8em" }}
                        ></JSONPretty>
                    </GeneralInfoModal>
                    {/* Modal for displaying detailed status -- ends here*/}
                </div>
            </div>
        </div>
    );
};

export default PVCPackageLogs;
